import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type ChatbotIngestionTasksResponse, type TaskPollingResponse } from './api'

// ── Query Keys ──────────────────────────────────────────────────────────────

export const queryKeys = {
    chatbots: ['chatbots'] as const,
    chatbot: (id: string) => ['chatbots', id] as const,
    chatbotFiles: (chatbotId: string) => ['chatbots', chatbotId, 'files'] as const,
    chatbotQnAFiles: (chatbotId: string) => ['chatbots', chatbotId, 'qna'] as const,
    chatbotIngestionTasks: (chatbotId: string) => ['chatbots', chatbotId, 'ingestion-tasks'] as const,
    ingestionPolling: ['ingestion-polling'] as const,
}

// ── Queries ─────────────────────────────────────────────────────────────────

export function useChatbots() {
    return useQuery({
        queryKey: queryKeys.chatbots,
        queryFn: ({ signal }) => api.getChatbots(signal),
    })
}

export function useSortedChatbots() {
    return useQuery({
        queryKey: queryKeys.chatbots,
        queryFn: ({ signal }) => api.getChatbots(signal),
        select: (data) =>
            [...data].sort((a, b) => {
                const hasPublishA = !!a.publishDate
                const hasPublishB = !!b.publishDate
                if (hasPublishA && hasPublishB) {
                    return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
                }
                if (hasPublishA) return -1
                if (hasPublishB) return 1
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            }),
    })
}

export function useChatbot(id: string | undefined) {
    return useQuery({
        queryKey: queryKeys.chatbot(id!),
        queryFn: () => api.getChatbot(id!),
        enabled: !!id,
    })
}

export function useChatbotFiles(chatbotId: string | undefined) {
    return useQuery({
        queryKey: queryKeys.chatbotFiles(chatbotId!),
        queryFn: () => api.getChatbotFiles(chatbotId!),
        enabled: !!chatbotId,
    })
}

export function useChatbotQnAFiles(chatbotId: string) {
    return useQuery({
        queryKey: queryKeys.chatbotQnAFiles(chatbotId),
        queryFn: () => api.getChatbotQnAFiles(chatbotId),
        enabled: !!chatbotId,
    })
}

export function useIngestionTasks(chatbotId: string | undefined, type: 'KNOWLEDGE_BASE' | 'QNA' = 'KNOWLEDGE_BASE') {
    return useQuery({
        queryKey: queryKeys.chatbotIngestionTasks(chatbotId!),
        queryFn: () => api.getChatbotIngestionTasksStatus(chatbotId!, { latestPerFile: true, type }),
        enabled: !!chatbotId,
        refetchInterval: (query) => {
            const data = query.state.data
            if (!data) return 5000
            const hasInFlight = data.tasks.some(
                (t) => t.status === 'PENDING' || t.status === 'PROCESSING'
            )
            return hasInFlight ? 5000 : false
        },
    })
}

export function useIngestionPolling() {
    return useQuery({
        queryKey: queryKeys.ingestionPolling,
        queryFn: () => api.getAllChatbotsIngestionPollingMap(),
        refetchInterval: 5000,
        select: (items): Record<string, TaskPollingResponse> => {
            const map: Record<string, TaskPollingResponse> = {}
            for (const item of items) {
                const chatbotId = item.chatbot_id.toString().padStart(3, '0')
                map[`CB${chatbotId}`] = item
            }
            return map
        },
    })
}

// ── Mutations ───────────────────────────────────────────────────────────────

export function useUpdateChatbotStatus() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, status }: { id: string; status: 'Active' | 'Inactive' }) =>
            api.updateChatbotStatus(id, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbots })
        },
    })
}

export function useSaveChatbot() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: Parameters<typeof api.saveChatbot>[0]) => api.saveChatbot(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbots })
        },
    })
}

export function useUploadQnAFile(chatbotId: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (file: File) => api.uploadChatbotQnAFile(chatbotId, file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbotQnAFiles(chatbotId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbotIngestionTasks(chatbotId) })
        },
    })
}

export function useDeleteQnAFile(chatbotId: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (fileId: string) => api.deleteChatbotQnAFile(chatbotId, fileId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbotQnAFiles(chatbotId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.chatbotIngestionTasks(chatbotId) })
        },
    })
}
